import { captureRemixErrorBoundaryError } from "@sentry/remix";
import type { LinksFunction } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useLocation,
  isRouteErrorResponse,
} from "@remix-run/react";
import { m } from "app/generated/i18n/messages";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://cdn.shopify.com/" },
  {
    rel: "stylesheet",
    href: "https://cdn.shopify.com/static/fonts/inter/v4/styles.css",
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const languageTag =
    new URLSearchParams(useLocation().search).get("locale") || "en";

  return (
    <html lang={languageTag}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);

  // Handle 404 errors specifically
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <html>
        <head>
          <title>404 - Page not found</title>
          <Meta />
          <Links />
        </head>
        <body>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              fontFamily:
                "-apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif",
              backgroundColor: "#f6f6f7",
              padding: "20px",
            }}
          >
            <img
              src="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              alt=""
              style={{ marginBottom: "24px", maxWidth: "120px" }}
            />
            <h1
              style={{
                fontSize: "24px",
                fontWeight: "600",
                margin: "0 0 8px 0",
                color: "#202223",
              }}
            >
              {m.notFoundHeading()}
            </h1>
            <p
              style={{
                fontSize: "16px",
                color: "#6d7175",
                margin: "0 0 24px 0",
                textAlign: "center",
                maxWidth: "400px",
                lineHeight: "1.4",
              }}
            >
              {m.notFoundMessage()}
            </p>
            <a
              href="/app"
              style={{
                display: "inline-block",
                backgroundColor: "#008060",
                color: "white",
                padding: "12px 24px",
                textDecoration: "none",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: "500",
              }}
            >
              {m.notFoundBackButton()}
            </a>
          </div>
          <Scripts />
        </body>
      </html>
    );
  }

  // Fall back to generic error for other errors
  return (
    <html>
      <head>
        <title>Something went wrong</title>
        <Meta />
        <Links />
      </head>
      <body>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            fontFamily:
              "-apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif",
            backgroundColor: "#f6f6f7",
            padding: "20px",
          }}
        >
          <img
            src="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            alt=""
            style={{ marginBottom: "24px", maxWidth: "120px" }}
          />
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "600",
              margin: "0 0 8px 0",
              color: "#202223",
            }}
          >
            {m.errorBoundaryHeading()}
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "#6d7175",
              margin: "0",
              textAlign: "center",
              maxWidth: "400px",
              lineHeight: "1.4",
            }}
          >
            {m.errorBoundaryMessage()}
          </p>
        </div>
        <Scripts />
      </body>
    </html>
  );
};

export default function App() {
  return <Outlet />;
}
